/**
 * CRUD de perfiles de importación (Excel → SUENLACE) para facturas emitidas.
 * Cada perfil define cómo se interpreta la Excel que un ERP del cliente genera.
 */

import { supabase } from "@/integrations/supabase/client";

/** Campos lógicos a los que cada columna del Excel puede mapearse. */
export type IssuedFieldKey =
  | "invoiceDate"
  | "series"
  | "invoiceNumber"
  | "customerName"
  | "customerTaxId"
  | "customerAccount"
  | "taxableBase"
  | "vatRate"
  | "vatAmount"
  | "reRate"
  | "reAmount"
  | "irpfRate"
  | "irpfAmount"
  | "totalAmount"
  | "salesAccount"
  | "postalCode"
  | "paymentMethod";

export const ISSUED_FIELDS: { key: IssuedFieldKey; label: string; required: boolean; hint?: string }[] = [
  { key: "invoiceDate",     label: "Fecha de factura",        required: true,  hint: "Fecha que se usa también como fecha contable." },
  { key: "series",          label: "Serie",                   required: false, hint: "Prefijo de serie del ERP. Si está vacío se usa el nº de factura." },
  { key: "invoiceNumber",   label: "Nº de factura",           required: true },
  { key: "customerName",    label: "Cliente — Nombre",        required: true },
  { key: "customerTaxId",   label: "Cliente — NIF",           required: true },
  { key: "customerAccount", label: "Cliente — Cuenta (430)",  required: false, hint: "Si está vacía se usa la cuenta por defecto del perfil." },
  { key: "taxableBase",     label: "Base imponible",          required: true },
  { key: "vatRate",         label: "% IVA",                   required: false, hint: "Si está vacío se infiere de cuota / base." },
  { key: "vatAmount",       label: "Cuota IVA",               required: true },
  { key: "reRate",          label: "% Recargo Equivalencia",  required: false, hint: "% RE por tramo de IVA (se emite en pos. 135/140 del registro 9)." },
  { key: "reAmount",        label: "Cuota Recargo Equivalencia", required: false },
  { key: "irpfRate",        label: "% IRPF",                  required: false },
  { key: "irpfAmount",      label: "Cuota IRPF",              required: false },
  { key: "totalAmount",     label: "Total factura",           required: true },
  { key: "salesAccount",    label: "Cuenta de venta (700)",   required: false, hint: "Si está vacía se usa la cuenta por defecto del perfil." },
  { key: "postalCode",      label: "Código postal cliente",   required: false },
  { key: "paymentMethod",   label: "Forma de cobro",          required: false, hint: "Texto tipo CONTADO / GIRO / TARJETA. Se usa para el asiento de cobro." },
];

/** Mapeo columna del Excel → campo lógico. La columna se referencia por su LETRA (A, B, AB…). */
export type IssuedColumnMap = Partial<Record<IssuedFieldKey, string>>;

export interface IssuedDefaults {
  /** Cuenta de cliente por defecto cuando la Excel no la trae. Ej: "43000000". */
  defaultCustomerAccount?: string;
  /** Cuenta de venta por defecto cuando la Excel no la trae. Ej: "70000000". */
  defaultSalesAccount?: string;
  /** Formato de fecha: "auto" detecta, otros formatean explícitamente. */
  dateFormat?: "auto" | "dd/mm/yyyy" | "yyyy-mm-dd" | "mm/dd/yyyy";
  /** Separador decimal en celdas de texto. "auto" intenta detectar. */
  decimalSeparator?: "auto" | "," | ".";
  /** Si true, las líneas con total negativo se marcan como abono / rectificativa. */
  negativeMeansRefund?: boolean;
  /** Mapa forma de pago (normalizada UPPERCASE sin acentos) → cuenta de tesorería. Vacío = no genera asiento de cobro para esa forma. */
  paymentAccountMap?: Record<string, string>;
  /** Si true (default), se genera asiento de cobro cuando la forma de pago está en el mapa. */
  generatePaymentEntry?: boolean;
}

export interface IssuedInvoiceMapping {
  id: string;
  /** Legacy: ya no se usa. Las plantillas son globales por asesoría. */
  company_id: string | null;
  organization_id: string;
  name: string;
  erp_label: string;
  sheet_index: number;
  /** Fila 1-indexada donde están las cabeceras de columna. */
  header_row: number;
  column_map: IssuedColumnMap;
  defaults: IssuedDefaults;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

const TABLE = "issued_invoice_mappings" as const;

// Casteamos como `any` porque la regeneración de tipos de supabase puede no
// haber llegado todavía cuando se compila este archivo por primera vez.
type SupabaseAny = ReturnType<typeof supabase.from>;
const table = () => (supabase.from(TABLE as unknown as never) as unknown) as SupabaseAny;

/** Obtiene la organización (asesoría) del usuario actual. */
const getCurrentOrgId = async (): Promise<string | null> => {
  const { data: userResp } = await supabase.auth.getUser();
  const userId = userResp.user?.id;
  if (!userId) return null;
  const { data } = await supabase
    .from("profiles")
    .select("organization_id")
    .eq("id", userId)
    .maybeSingle();
  return (data as { organization_id: string | null } | null)?.organization_id ?? null;
};

/**
 * Carga TODAS las plantillas de la asesoría del usuario. Son globales:
 * no dependen de la empresa cliente.
 */
export const loadAllIssuedMappings = async (): Promise<IssuedInvoiceMapping[]> => {
  const { data, error } = await table()
    .select("*")
    .order("name");
  if (error) {
    console.error("Error loading issued invoice mappings:", error);
    return [];
  }
  return (data || []) as IssuedInvoiceMapping[];
};

/** @deprecated Las plantillas son globales; usa loadAllIssuedMappings. */
export const loadIssuedMappings = async (_companyId: string): Promise<IssuedInvoiceMapping[]> => {
  return loadAllIssuedMappings();
};

export const createIssuedMapping = async (
  payload: Omit<IssuedInvoiceMapping, "id" | "created_at" | "updated_at" | "created_by" | "company_id" | "organization_id">,
): Promise<{ data?: IssuedInvoiceMapping; error?: string }> => {
  const { data: userResp } = await supabase.auth.getUser();
  const userId = userResp.user?.id ?? null;
  const orgId = await getCurrentOrgId();
  if (!orgId) return { error: "No se pudo determinar la asesoría del usuario." };
  const { data, error } = await table()
    .insert({ ...payload, organization_id: orgId, company_id: null, created_by: userId })
    .select("*")
    .single();
  if (error) return { error: error.message };
  return { data: data as IssuedInvoiceMapping };
};

export const updateIssuedMapping = async (
  id: string,
  patch: Partial<Omit<IssuedInvoiceMapping, "id" | "company_id" | "organization_id" | "created_at" | "updated_at" | "created_by">>,
): Promise<{ error?: string }> => {
  const { error } = await table().update(patch).eq("id", id);
  return { error: error?.message };
};

export const deleteIssuedMapping = async (id: string): Promise<{ error?: string }> => {
  const { error } = await table().delete().eq("id", id);
  return { error: error?.message };
};

